import React, { FC, useEffect } from 'react';

import {
  Container,
  Bage,
  Message,
  StyledClose,
  StyledImg,
  CloseBtnWrapper,
  Title,
  Subscription,
} from './styled';

export type ToastAppearance = 'success' | 'error' | 'info' | 'warning';
export type ToastSize = 'small' | 'medium' | 'large';

interface ToastProps {
  appearance: ToastAppearance;
  size?: ToastSize;
  children:
    | string
    | {
        link?: string | ((url: string) => string);
        message: string;
        img?: string;
        image?: string;
        title?: string;
      };
  subscription?: string;
  onDismiss?: () => void;
  autoDismiss?: boolean;
  autoDismissTimeout?: number;
  id?: string;
}

const Toast: FC<ToastProps> = ({
  appearance,
  size = 'medium',
  children,
  onDismiss,
  autoDismiss = true,
  autoDismissTimeout = 5000,
  subscription,
  id,
}) => {
  useEffect(() => {
    if (autoDismiss && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoDismissTimeout);

      return () => clearTimeout(timer);
    }
  }, [autoDismiss, autoDismissTimeout, onDismiss]);

  const getAriaLabel = () => {
    const message = typeof children === 'string' ? children : children.message;
    return `${appearance} notification: ${message}`;
  };

  return (
    <Container
      id={id}
      role="alert"
      aria-live="polite"
      aria-label={getAriaLabel()}
      data-appearance={appearance}
      data-size={size}
    >
      {children.link && (
        <a
          href={children.link as string}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open link in new tab"
        />
      )}
      <Bage appearance={appearance} />
      {typeof children === 'string' ? (
        <Message>{children}</Message>
      ) : (
        <>
          {(children.img || children.image) && (
            <StyledImg
              src={children.img || children.image}
              alt=""
              aria-hidden="true"
            />
          )}
          <Message>
            {children.title && <Title>{children.title}</Title>}
            <div dangerouslySetInnerHTML={{ __html: children.message }} />
            {subscription && <Subscription>{subscription}</Subscription>}
          </Message>
        </>
      )}
      <CloseBtnWrapper
        onClick={onDismiss}
        role="button"
        tabIndex={0}
        aria-label="Dismiss notification"
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onDismiss?.();
          }
        }}
      >
        <StyledClose />
      </CloseBtnWrapper>
    </Container>
  );
};

export default Toast;
